import { useCallback, useEffect, useRef, useState } from "react";

// The browser's built-in speech-to-text (Chrome, Edge, Safari). Not in every
// browser (e.g. Firefox), so callers hide the microphone when unsupported.
type SpeechResultEvent = {
  resultIndex: number;
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
};
type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
};

const getRecognitionClass = (): (new () => Recognition) | undefined => {
  if (typeof window === "undefined") return undefined;
  const w = window as unknown as {
    SpeechRecognition?: new () => Recognition;
    webkitSpeechRecognition?: new () => Recognition;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition;
};

/** Joins dictated words onto the text already typed, with one space between. */
export const appendDictation = (text: string, spoken: string) => {
  const words = spoken.trim();
  if (!words) return text;
  return text && !/\s$/.test(text) ? `${text} ${words}` : `${text}${words}`;
};

/** Dictation: each finished phrase is passed to onText. */
export const useDictation = (onText: (spoken: string) => void) => {
  const supported = !!getRecognitionClass();
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<Recognition | null>(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    const RecognitionClass = getRecognitionClass();
    if (!RecognitionClass) return;
    const recognition = new RecognitionClass();
    recognition.lang = navigator.language || "en-US";
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) onTextRef.current(result[0].transcript);
      }
    };
    recognition.onerror = (event) => {
      setError(
        event.error === "not-allowed"
          ? "Microphone access was blocked. Allow it in your browser to dictate."
          : "Dictation stopped. Please try again.",
      );
    };
    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };
    setError(null);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, []);

  useEffect(() => () => recognitionRef.current?.stop(), []);

  return { supported, isListening, error, start, stop };
};
